import { customerRecoveryCapability } from './customer-recovery';
import { clearResume } from './session-resume';
import { adminSetupCapability } from './admin-setup';
import {
  hasPinSession,
  pinCredential,
  clearPinSession,
  takePinSessionForCallback,
} from './pin-session';
import { resolveOemRoute } from '../features/oem/routes';
import {
  capturePasswordCallback,
  passwordCallbackPath,
  verifyPasswordCallback,
} from './auth-setup';
import { requiredValue } from './oem-display';
import { createClient, isAuthRetryableFetchError } from '@supabase/supabase-js';
export interface OemContext {
  readonly projectId: string;
  readonly organizationId: string;
  readonly customerId: string;
  readonly audience: 'staff' | 'customer';
  readonly role: string;
  readonly actions: readonly string[];
  readonly readBundles: readonly string[];
  readonly label: string;
  readonly validUntil?: string;
}
export interface ParentContext {
  readonly organizationId: string;
  readonly customerId?: string;
  readonly role: string;
  readonly actions: readonly string[];
  readonly label?: string;
}
export interface ActiveSession {
  readonly userId: string;
  readonly organizationId: string;
  readonly permissions: readonly string[];
  readonly contexts: readonly OemContext[];
  readonly demo: boolean;
  readonly offline?: boolean;
  readonly signInMethod?: 'pin' | 'password';
  readonly organizationContexts: readonly ParentContext[];
  readonly customerContexts: readonly ParentContext[];
}
export const runtimeMode = import.meta.env.VITE_RUNTIME_MODE;
const callback =
  runtimeMode === 'connected' && !adminSetupCapability && !customerRecoveryCapability
    ? capturePasswordCallback(new URL(window.location.href), sessionStorage, () =>
        history.replaceState(null, '', passwordCallbackPath),
      )
    : null;
// Retire only the old app session before the callback installs or authorizes its Bearer.
// Reloaded pending setup never restores the prior PIN identity.
const previousPin = takePinSessionForCallback(
  Boolean(adminSetupCapability) ||
    Boolean(customerRecoveryCapability) ||
    callback?.pending === true,
);
if (previousPin) {
  void fetch(`${import.meta.env.VITE_API_URL}/v1/staff-pin/logout`, {
    method: 'POST',
    headers: {
      Authorization: `PinSession ${previousPin.token}`,
      'Content-Type': 'application/json',
    },
    body: '{}',
    cache: 'no-store',
    referrerPolicy: 'no-referrer',
  }).catch(() => undefined);
}
export const passwordSetupRequired = callback?.pending ?? false;
const authStorageKey = import.meta.env.VITE_SUPABASE_URL
  ? `sb-${new URL(import.meta.env.VITE_SUPABASE_URL).hostname.split('.')[0]}-auth-token`
  : '';
export function clearBrowserPasswordSession() {
  if (authStorageKey) localStorage.removeItem(authStorageKey);
}
export const authClient =
  runtimeMode === 'connected' &&
  import.meta.env.VITE_SUPABASE_URL &&
  import.meta.env.VITE_SUPABASE_ANON_KEY
    ? createClient(import.meta.env.VITE_SUPABASE_URL, import.meta.env.VITE_SUPABASE_ANON_KEY, {
        auth: {
          detectSessionInUrl: false,
          persistSession: !callback?.pending && !customerRecoveryCapability,
          storageKey: authStorageKey,
        },
      })
    : null;
// Finish sealing the previous browser identity before a capability can enroll another account.
export const adminSetupRetirement =
  (adminSetupCapability || customerRecoveryCapability) && authClient
    ? (async () => {
        const previous = await authClient.auth.getSession();
        if (previous.data.session) {
          const { sealCachedIdentity } = await import('./oem-offline');
          await sealCachedIdentity(previous.data.session.user.id);
          const result = await authClient.auth.signOut({ scope: 'local' });
          if (result.error) return false;
        }
        clearBrowserPasswordSession();
        localStorage.removeItem('sqi-active-project');
        return true;
      })().catch(() => false)
    : Promise.resolve(true);
export const passwordCallbackVerification =
  authClient && callback?.pending
    ? verifyPasswordCallback(authClient.auth, callback, sessionStorage).then(
        () => true,
        () => false,
      )
    : Promise.resolve(false);
export async function getActiveSession(): Promise<ActiveSession | null> {
  if (adminSetupCapability || customerRecoveryCapability) return null;
  if (runtimeMode === 'fixture') {
    if (localStorage.getItem('sqi-fixture-signed-out') === 'true') return null;
    const role = localStorage.getItem('sqi-fixture-role') ?? 'coordinator';
    const customer = role.startsWith('customer');
    return {
      userId: '00000000-0000-4000-8000-000000000101',
      organizationId: '00000000-0000-4000-8000-000000000001',
      permissions: [],
      demo: true,
      organizationContexts: customer
        ? []
        : [
            {
              organizationId: '00000000-0000-4000-8000-000000000001',
              role,
              actions: ['coordinator', 'owner', 'admin'].includes(role)
                ? ['create_customer', 'create_brand', 'create_project', 'create_sku']
                : [],
            },
          ],
      customerContexts: [],
      contexts: [
        {
          projectId: '00000000-0000-4000-8000-000000000201',
          organizationId: '00000000-0000-4000-8000-000000000001',
          customerId: '00000000-0000-4000-8000-000000000202',
          audience: customer ? 'customer' : 'staff',
          role,
          actions: fixtureActions(role),
          readBundles: fixtureBundles(role),
          label: 'Mali Studio · OEM-DEMO-001',
        },
      ],
    };
  }
  if (hasPinSession()) {
    const credential = pinCredential();
    if (!credential || passwordSetupRequired) return null;
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/v1/oem/session`, {
        headers: { Authorization: `PinSession ${credential.token}` },
        cache: 'no-store',
        referrerPolicy: 'no-referrer',
      });
      if (!response.ok) return null;
      const result = (await response.json()) as {
        data: {
          userId: string;
          signInMethod: string;
          contexts: OemContext[];
          organizationContexts: ParentContext[];
          customerContexts: ParentContext[];
        };
      };
      if (
        result.data.signInMethod !== 'pin' ||
        result.data.customerContexts.length ||
        result.data.contexts.some((c) => c.audience !== 'staff')
      )
        return null;
      const selected = resolveOemRoute(window.location.pathname).projectId;
      const context =
        result.data.contexts.find((c) => c.projectId === selected) ??
        (result.data.contexts.length === 1 ? result.data.contexts[0] : undefined);
      return {
        userId: result.data.userId,
        organizationId: context?.organizationId ?? '',
        permissions: context?.actions ?? [],
        contexts: result.data.contexts,
        demo: false,
        signInMethod: 'pin',
        organizationContexts: result.data.organizationContexts,
        customerContexts: [],
      };
    } catch {
      return null;
    }
  }
  if (!authClient || passwordSetupRequired) return null;
  const offlineSession = async () => {
    const { data } = await authClient.auth.getSession();
    if (!data.session) return null;

    const { restoreVaultKey } = await import('./oem-vault');
    if (!(await restoreVaultKey(data.session.user.id, data.session.refresh_token))) return null;
    const { cachedSession } = await import('./oem-offline');
    const cached = await cachedSession(data.session.user.id);
    return cached ? { ...cached, offline: true } : null;
  };
  if (!navigator.onLine) return offlineSession();
  const { data, error } = await authClient.auth.getUser();
  if (error && isAuthRetryableFetchError(error) && error.status === 0) return offlineSession();
  if (error || !data.user) return null;
  const session = await authClient.auth.getSession();
  const token = session.data.session?.access_token;
  if (!token) return null;
  let response: Response;
  try {
    response = await fetch(`${import.meta.env.VITE_API_URL}/v1/oem/session`, {
      headers: { Authorization: `Bearer ${token}` },
      cache: 'no-store',
    });
  } catch (e) {
    if (e instanceof TypeError) return offlineSession();
    throw e;
  }
  if (!response.ok) return null;
  const result = (await response.json()) as {
    data: {
      userId: string;
      signInMethod?: 'pin' | 'password';
      contexts: OemContext[];
      organizationContexts: ParentContext[];
      customerContexts: ParentContext[];
    };
  };
  const selected =
    resolveOemRoute(window.location.pathname).projectId ??
    localStorage.getItem('sqi-active-project');
  const context =
    result.data.contexts.find((c) => c.projectId === selected) ??
    (result.data.contexts.length === 1 ? result.data.contexts[0] : undefined);
  const verified: ActiveSession = {
    userId: result.data.userId,
    organizationId: context?.organizationId ?? '',
    permissions: context?.actions ?? [],
    contexts: result.data.contexts,
    demo: false,
    signInMethod: result.data.signInMethod ?? 'password',
    organizationContexts: result.data.organizationContexts,
    customerContexts: result.data.customerContexts,
  };
  if (verified.signInMethod === 'pin') return verified;
  const { vaultPolicy, retainVaultKey } = await import('./oem-vault');
  if (vaultPolicy()) {
    const keyResponse = await fetch(`${import.meta.env.VITE_API_URL}/v1/oem/offline-key`, {
      headers: { Authorization: `Bearer ${token}` },
      cache: 'no-store',
    });
    if (!keyResponse.ok) throw Error('offline_policy_unconfigured');
    const keyResult = (await keyResponse.json()) as {
      data: {
        key: string;
      };
    };
    await retainVaultKey(
      verified.userId,
      keyResult.data.key,
      requiredValue(session.data.session).refresh_token,
    );
  }
  const { retainVerifiedSession } = await import('./oem-offline');
  await retainVerifiedSession(verified);
  return verified;
}
export async function sessionAuthorization(): Promise<string> {
  if (hasPinSession()) {
    const credential = pinCredential();
    if (!credential) throw Error('authentication_required');
    return `PinSession ${credential.token}`;
  }
  const session = await authClient?.auth.getSession();
  if (!session?.data.session) throw Error('authentication_required');
  return `Bearer ${session.data.session.access_token}`;
}
export async function signOutSession(): Promise<void> {
  clearResume();
  if (hasPinSession()) {
    const credential = pinCredential();
    if (credential) {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/v1/staff-pin/logout`, {
        method: 'POST',
        headers: {
          Authorization: `PinSession ${credential.token}`,
          'Content-Type': 'application/json',
        },
        body: '{}',
        cache: 'no-store',
        referrerPolicy: 'no-referrer',
      });
      if (!response.ok) throw Error('sign_out_unavailable');
    }
    clearPinSession();
    localStorage.removeItem('sqi-active-project');
    globalThis.dispatchEvent(new Event('sqi-session-changed'));
    return;
  }
  const { data } = authClient ? await authClient.auth.getSession() : { data: { session: null } };
  if (data.session) {
    const { sealCachedIdentity } = await import('./oem-offline');
    await sealCachedIdentity(data.session.user.id);
  }
  await authClient?.auth.signOut();
  if (runtimeMode === 'fixture') localStorage.setItem('sqi-fixture-signed-out', 'true');
  localStorage.removeItem('sqi-active-project');
  globalThis.dispatchEvent(new Event('sqi-session-changed'));
}
export const fixtureRoles = [
  'coordinator',
  'rd',
  'rd_checker',
  'qa',
  'owner',
  'finance',
  'purchasing',
  'designer',
  'admin',
  'auditor',
  'production',
  'customer-requester',
  'customer-sensory',
  'customer-designer',
  'customer-commercial',
  'customer-finance',
] as const;
function fixtureActions(role: string): string[] {
  const author = ['create', 'save_draft', 'revise', 'submit'];
  const roleActions: Record<string, string[]> = {
    coordinator: [
      ...author,
      'prepare',
      'dispatch',
      'receive',
      'evaluate',
      'adopt_target',
      'issue_quote',
      'reconcile_po',
      'confirm_order',
    ],
    rd: [...author, 'prepare', 'dispatch', 'receive', 'evaluate', 'acknowledge_handover'],
    rd_checker: ['review_technical'],
    qa: ['review_technical', 'review_regulatory', 'resolve_hold'],
    owner: ['review_commercial', 'authorize_commitment'],
    finance: [...author, 'verify_receipt', 'allocate_payment', 'reverse_allocation'],
    purchasing: [...author, 'authorize_commitment'],
    designer: author,
    admin: ['admin'],
    auditor: [],
    production: [...author, 'acknowledge_handover'],
    'customer-requester': author,
    'customer-sensory': ['decide_sensory', 'agree_criteria'],
    'customer-designer': [...author, 'decide_design'],
    'customer-commercial': ['accept_quote'],
    'customer-finance': author,
  };
  return roleActions[role] ?? [];
}
function fixtureBundles(role: string): string[] {
  const map: Record<string, string[]> = {
    coordinator: ['shared', 'design', 'commercial'],
    rd: ['shared', 'technical', 'design'],
    rd_checker: ['shared', 'technical', 'design'],
    qa: ['shared', 'technical', 'design'],
    owner: ['shared', 'cost', 'commercial', 'design'],
    finance: ['shared', 'finance', 'commercial'],
    purchasing: ['shared', 'design', 'cost'],
    designer: ['shared', 'design'],
    admin: ['shared'],
    auditor: ['shared', 'technical', 'design', 'commercial', 'finance', 'cost'],
    production: ['shared', 'technical', 'design'],
    'customer-requester': ['shared'],
    'customer-sensory': ['shared'],
    'customer-designer': ['shared', 'design'],
    'customer-commercial': ['shared', 'commercial'],
    'customer-finance': ['shared', 'finance', 'commercial'],
  };
  return map[role] ?? [];
}
